"use client";

import { useState } from "react";
import type { ReactNode } from "react";
import type { Session } from "next-auth";
import { useRouter } from "next/navigation";

import { getPlan } from "@/lib/api/plan";
import { cn, errorIntoString } from "@/lib/utils";
import { OnboardingStages, type Plan, UserPlan } from "@/types";
import { isSafeStripeUrl } from "@/lib/security/safe-href";
import { useUpdateUserMutation } from "@/features/user/userInjection";
import { useGetHostingUsageQuery } from "@/features/order/orderApiInjection";
import type { SubscriptionFrequency } from "@/app/subscription/[frequency]/types";
import {
	useActivatePlanMutation,
	useDowngradePlanMutation,
	useUpgradePlanMutation,
} from "@/features/plan/planInjection";
import { Currency } from "@/lib/utils/currency";
import { useCurrency } from "@/lib/contexts/CurrencyContext";
import {
	buildPlanGa4Item,
	ga4SelectItem,
	ga4Subscribe,
	goToStripeAfterCheckout,
} from "@/lib/analytics/ga4-ecommerce";
import {
	getHostingPlanTier,
	hostingTierCoversCount,
} from "@/lib/hosting/hosting-plan-tier.util";

import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import PlanDowngradeConfirmDialog from "@/components/cards/PlanCard/PlanDowngradeConfirmDialog";
import PlanHostingDowngradeBlockedDialog from "@/components/cards/PlanCard/PlanHostingDowngradeBlockedDialog";

type ShellProps = {
	disabled: boolean;
	isSelected?: boolean;
	buttonColor: string;
	onClick?: () => void;
	children: ReactNode;
};

export function PlanButtonShell({
	disabled,
	isSelected = false,
	buttonColor,
	onClick,
	children,
}: ShellProps) {
	return (
		<Button
			type="button"
			onClick={isSelected ? undefined : onClick}
			disabled={isSelected || disabled}
			aria-disabled={isSelected || disabled}
			tabIndex={isSelected ? -1 : undefined}
			style={isSelected ? undefined : { backgroundColor: buttonColor }}
			className={cn(
				"w-full py-6 text-sm3 font-semibold",
				isSelected
					? "cursor-default bg-light-grey text-white shadow-none hover:bg-light-grey hover:text-white disabled:opacity-100"
					: "custom-box-shadow text-white disabled:opacity-15",
			)}
		>
			{children}
		</Button>
	);
}

type Props = {
	plan: Plan;
	plans?: Plan[];
	session: Session | null;
	updateSession: (data?: any) => Promise<Session | null>;
	frequency: SubscriptionFrequency;
};

function planTierIndex(plans: Plan[], planId: string | null | undefined): number {
	if (!planId) return -1;
	return plans.findIndex((p) => p._id === planId);
}

function isSafePlanUpgradeRedirectUrl(url: string | null | undefined): boolean {
	if (isSafeStripeUrl(url)) return true;
	if (url == null || typeof url !== "string" || !url.trim()) return false;
	try {
		const parsed = new URL(url.trim(), window.location.origin);
		const path = parsed.pathname.endsWith("/") && parsed.pathname.length > 1
			? parsed.pathname.slice(0, -1)
			: parsed.pathname;
		return (parsed.protocol === "https:" || parsed.protocol === "http:") && path === "/success";
	} catch {
		return false;
	}
}

export default function PlanButton({ plan, plans, session, updateSession, frequency }: Props) {
	const router = useRouter();
	const { toast } = useToast();
	const currency = useCurrency();
	const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
	const [hostingBlockedDialogOpen, setHostingBlockedDialogOpen] = useState(false);

	const [updateUser, { isLoading: isUserUpdating }] = useUpdateUserMutation();
	const [activatePlan, { isLoading: isPlanCreating }] = useActivatePlanMutation();
	const [upgradePlan, { isLoading: isPlanUpgrading }] = useUpgradePlanMutation();
	const [downgradePlan, { isLoading: isPlanDowngrading }] = useDowngradePlanMutation();

	const planKind = plan.planType ?? "subscription";
	const { data: hostingUsage } = useGetHostingUsageQuery(undefined, {
		skip: planKind !== "hosting" || !session?.idToken,
	});

	const isLoading = isUserUpdating || isPlanCreating || isPlanUpgrading || isPlanDowngrading;

	const priceForFrequency = plan.prices.find((p) => p.duration.toLowerCase() === frequency);
	const priceAmount = priceForFrequency?.amounts?.[currency] ?? priceForFrequency?.amount ?? 0;
	const isPlanFree = priceAmount === 0;

	const isUserInFreePlan =
		session?.user.plan.duration === UserPlan.free ||
		session?.user.plan.duration === UserPlan.notSelected;

	const tierList = plans ?? [];
	const isHostingSelected =
		planKind === "hosting" &&
		(plan._id === session?.user.hostingPlan?.planID ||
			plan._id === session?.user.plan.planID);
	const isSelected =
		(planKind === "subscription" && plan._id === session?.user.plan.planID) ||
		isHostingSelected ||
		(isUserInFreePlan && isPlanFree);

	const activePlanIdForTiers = (() => {
		if (!session) return null;
		if (planKind === "hosting") {
			const hostingId = session.user.hostingPlan?.planID;
			if (hostingId && tierList.some((p) => p._id === hostingId)) return hostingId;
			if (
				session.user.plan.planID &&
				tierList.some((p) => p._id === session.user.plan.planID)
			) {
				return session.user.plan.planID;
			}
			return null;
		}
		if (planKind === "subscription") {
			const subscriptionPlanId = session.user.plan?.planID;
			if (
				subscriptionPlanId &&
				tierList.some((p) => p._id === subscriptionPlanId)
			) {
				return subscriptionPlanId;
			}
			return null;
		}
		return null;
	})();
	const userPlanIndex = activePlanIdForTiers
		? planTierIndex(tierList, activePlanIdForTiers)
		: -1;
	const thisPlanIndex = planTierIndex(tierList, plan._id);
	const isHigherTier =
		userPlanIndex >= 0 && thisPlanIndex >= 0 && thisPlanIndex > userPlanIndex;
	const isLowerTier =
		userPlanIndex >= 0 && thisPlanIndex >= 0 && thisPlanIndex < userPlanIndex;

	const hostingEligibleCount = hostingUsage?.purchasedHostingEligibleServiceCount ?? 0;
	const hostingDowngradeBlocked =
		isLowerTier &&
		planKind === "hosting" &&
		!hostingTierCoversCount(getHostingPlanTier(plan), hostingEligibleCount);

	const tabDuration = frequency === "monthly" ? UserPlan.monthly : UserPlan.yearly;
	const downgradeDuration =
		planKind === "hosting" &&
		(session?.user.hostingPlan?.duration === UserPlan.monthly ||
			session?.user.hostingPlan?.duration === UserPlan.yearly)
			? session.user.hostingPlan.duration
			: session?.user.plan.duration === UserPlan.monthly ||
				  session?.user.plan.duration === UserPlan.yearly
				? session.user.plan.duration
				: tabDuration;

	const handleDowngradeClick = () => {
		if (hostingDowngradeBlocked) {
			setHostingBlockedDialogOpen(true);
			return;
		}
		setDowngradeDialogOpen(true);
	};

	const confirmDowngrade = async () => {
		if (!plan._id) return;
		const result = await downgradePlan({
			id: plan._id,
			duration: downgradeDuration,
			currency,
		});

		if ("error" in result) {
			toast({
				variant: "destructive",
				title: "Could not schedule downgrade",
				description: errorIntoString(result.error),
			});
			return;
		}

		setDowngradeDialogOpen(false);
		toast({
			title: "Downgrade scheduled",
			description: result.data.message,
		});
	};

	const handleClick = () => {
		ga4SelectItem({
			item_list_id: planKind === "hosting" ? "hosting_plans" : "subscription_plans",
			item_list_name: planKind === "hosting" ? "Hosting plans" : "Plans",
			items: [buildPlanGa4Item(plan, currency, frequency)],
		});
		if (isLowerTier) {
			void handleDowngradeClick();
			return;
		}
		void onPlanButtonClick(
			session,
			frequency,
			plan,
			currency,
			router,
			toast,
			updateUser,
			activatePlan,
			upgradePlan,
			updateSession,
		);
	};

	const showPlanButtonSpinner =
		(isUserUpdating || isPlanCreating || isPlanUpgrading) && !isPlanDowngrading;

	const getButtonText = () => {
		if (showPlanButtonSpinner) return <LoadingSpinner />;
		if (isSelected) return "Selected";
		if (isHigherTier) return "Upgrade";
		if (isLowerTier) return "Downgrade";
		return "Select";
	};

	return (
		<>
			<PlanButtonShell
				disabled={isLoading}
				isSelected={isSelected}
				buttonColor={plan.colors.buttonColor}
				onClick={handleClick}
			>
				{getButtonText()}
			</PlanButtonShell>

			<PlanDowngradeConfirmDialog
				open={downgradeDialogOpen}
				onOpenChange={setDowngradeDialogOpen}
				planName={plan.name}
				isLoading={isPlanDowngrading}
				onConfirm={() => void confirmDowngrade()}
			/>

			<PlanHostingDowngradeBlockedDialog
				open={hostingBlockedDialogOpen}
				onOpenChange={setHostingBlockedDialogOpen}
				planName={plan.name}
				hostedServiceCount={hostingEligibleCount}
			/>
		</>
	);
}

async function onPlanButtonClick(
	session: Session | null,
	frequency: SubscriptionFrequency,
	plan: Plan,
	currency: Currency,
	router: ReturnType<typeof useRouter>,
	toast: ReturnType<typeof useToast>["toast"],
	updateUser: ReturnType<typeof useUpdateUserMutation>[0],
	activatePlan: ReturnType<typeof useActivatePlanMutation>[0],
	upgradePlan: ReturnType<typeof useUpgradePlanMutation>[0],
	updateSession: Props["updateSession"],
) {
	if (session === null) return router.replace("/login");
	if (plan._id === session.user.plan.planID) return;

	const priceForFrequency = plan.prices.find((p) => p.duration.toLowerCase() === frequency);
	const priceAmount = priceForFrequency?.amounts?.[currency] ?? priceForFrequency?.amount ?? 0;
	const isSelectedPlanFree = priceAmount === 0;

	const isUserInFreePlan =
		session.user.plan.duration === UserPlan.free ||
		session.user.plan.duration === UserPlan.notSelected;
	const tabDuration = frequency === "monthly" ? UserPlan.monthly : UserPlan.yearly;
	const isHostingUpgrade =
		plan.planType === "hosting" &&
		session.user.plan.planID !== null &&
		!isUserInFreePlan;
	const duration =
		isHostingUpgrade &&
		(session.user.plan.duration === UserPlan.monthly ||
			session.user.plan.duration === UserPlan.yearly)
			? session.user.plan.duration
			: tabDuration;

	if (isSelectedPlanFree) {
		const result = await updateUser({
			userId: session.user.id,
			patchUser: {
				onboardingStages: OnboardingStages.PLAN_PURCHASED,
				plan: { duration: UserPlan.free, planID: plan._id },
			},
		});

		if ("error" in result) {
			const ex = errorIntoString(result.error);
			return toast({
				variant: "destructive",
				title: "Failed to update the selected plan",
				description: ex?.toString(),
			});
		}

		await updateSession({ ...session, user: result.data });

		ga4Subscribe({
			plan_id: plan._id,
			plan_name: plan.name,
			plan_type: plan.planType,
			frequency,
			value: 0,
			currency,
		});
		toast({ title: "Free Plan Selected" });
		return router.push("/success");
	}

	let currentPlanType: Plan["planType"] | undefined;
	if (session.user.plan.planID) {
		try {
			const current = await getPlan(session.user.plan.planID);
			currentPlanType = current.planType;
		} catch {
			currentPlanType = undefined;
		}
	}
	const isCrossTypeToSubscription =
		currentPlanType === "hosting" && plan.planType === "subscription";

	if (
		session.user.plan.planID !== null &&
		!isUserInFreePlan &&
		!isCrossTypeToSubscription
	) {
		const result = await upgradePlan({ id: plan._id ?? "", duration, currency });

		if ("error" in result) {
			const ex = errorIntoString(result.error);
			return toast({
				variant: "destructive",
				title: "Failed to upgrade to new plan",
				description: ex?.toString(),
			});
		}

		const url = result.data.checkoutUrl;
		if (!isSafePlanUpgradeRedirectUrl(url)) {
			return toast({
				variant: "destructive",
				title: "Invalid upgrade URL",
				description: "Please try again or contact support.",
			});
		}
		if (isSafeStripeUrl(url)) {
			const item = buildPlanGa4Item(plan, currency, frequency);
			ga4Subscribe({
				plan_id: plan._id,
				plan_name: plan.name,
				plan_type: plan.planType,
				frequency,
				value: item.price,
				currency,
			});
			goToStripeAfterCheckout(url, {
				currency,
				value: item.price ?? 0,
				payment_type: "card",
				items: [item],
			});
			return;
		}
		return router.push(url);
	}

	const result = await activatePlan({ id: plan._id ?? "", duration: duration, currency });

	if ("error" in result) {
		const ex = errorIntoString(result.error);
		return toast({
			variant: "destructive",
			title: "Failed to update the selected plan",
			description: ex,
		});
	}

	const checkoutUrl = result.data.checkoutUrl;
	if (!isSafeStripeUrl(checkoutUrl)) {
		return toast({
			variant: "destructive",
			title: "Invalid checkout URL",
			description: "Please try again or contact support.",
		});
	}
	const item = buildPlanGa4Item(plan, currency, frequency);
	ga4Subscribe({
		plan_id: plan._id,
		plan_name: plan.name,
		plan_type: plan.planType,
		frequency,
		value: item.price,
		currency,
	});
	goToStripeAfterCheckout(checkoutUrl, {
		currency,
		value: item.price ?? 0,
		payment_type: "card",
		items: [item],
	});
}
